import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GlassCard from "../components/GlassCard";
import GoldButton from "../components/GoldButton";
import GhostButton from "../components/GhostButton";

function FeatureCard({ title, body, accent }) {
  return (
    <GlassCard className="p-6">
      <div
        className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-lg"
        style={{
          background: accent === "gold" ? "rgba(212,175,55,0.15)" : "rgba(30,64,175,0.18)",
          border: accent === "gold" ? "1px solid rgba(212,175,55,0.35)" : "1px solid rgba(30,64,175,0.45)",
        }}
      >
        <span
          className="block h-2 w-2 rounded-full"
          style={{ background: accent === "gold" ? "#D4AF37" : "#1E40AF" }}
        />
      </div>
      <h3 className="text-base font-semibold text-white mb-1">{title}</h3>
      <p className="text-sm text-white/65 leading-relaxed">{body}</p>
    </GlassCard>
  );
}

export default function Landing() {
  const { t } = useTranslation();

  return (
    <section className="pt-6 pb-20 sm:pt-12">
      <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
        <div>
          <span
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs uppercase tracking-[0.18em] text-white/60"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
            Phase 1 · Auth + i18n
          </span>
          <h1
            className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] text-white"
            data-testid="hero-title"
          >
            <span className="text-[#D4AF37]">Nexa</span>lytics
            <span className="block text-white/85 mt-2 text-3xl sm:text-4xl lg:text-5xl">
              Prompt Auditor
            </span>
          </h1>
          <p
            className="mt-6 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
            data-testid="hero-tagline"
          >
            {t("landing.tagline")}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="cta-register">
              <GoldButton>{t("landing.ctaRegister")}</GoldButton>
            </Link>
            <Link to="/login" data-testid="cta-login">
              <GhostButton>{t("landing.ctaLogin")}</GhostButton>
            </Link>
          </div>
        </div>

        {/* Decorative glass panel */}
        <GlassCard className="p-7 lg:p-9">
          <div className="flex items-center justify-between mb-5">
            <span className="text-xs uppercase tracking-[0.18em] text-white/50">
              Nexa-Score
            </span>
            <span className="text-xs text-white/40">preview</span>
          </div>
          <div className="flex items-end gap-4">
            <div className="text-6xl sm:text-7xl font-display text-white tracking-tight">
              8.4
            </div>
            <div className="pb-2 text-sm text-white/50">/ 10</div>
          </div>
          <div className="mt-1 text-sm text-[#D4AF37]">Needs Revision</div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {[
              ["Clarity", 9.0, "#D4AF37"],
              ["Constraints", 7.0, "#1E40AF"],
              ["Efficiency", 8.5, "#D4AF37"],
              ["Resilience", 9.0, "#1E40AF"],
            ].map(([k, v, c]) => (
              <div key={k} className="rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3">
                <div className="text-xs uppercase tracking-wider text-white/45">{k}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-xl font-semibold text-white">{v.toFixed(1)}</span>
                  <span
                    className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden"
                    aria-hidden
                  >
                    <span
                      className="block h-full rounded-full"
                      style={{ width: `${(v / 10) * 100}%`, background: c }}
                    />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <div className="mt-20 grid gap-5 sm:grid-cols-3" data-testid="features-grid">
        <FeatureCard title={t("landing.feature1Title")} body={t("landing.feature1Body")} accent="gold" />
        <FeatureCard title={t("landing.feature2Title")} body={t("landing.feature2Body")} accent="blue" />
        <FeatureCard title={t("landing.feature3Title")} body={t("landing.feature3Body")} accent="gold" />
      </div>
    </section>
  );
}
